import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Check, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Progress } from "@/components/ui/progress";
const SHARED_DATA = {
  address: "Townsville, QLD, North Queensland Region",
  phone: "0409 908 999",
  email: "admin@anvpainting.com.au",
  hours: "Monday to Friday: 8am - 5pm",
  business_name: "ANV Painting"
};
const SERVICE_OPTIONS = ["Interior Painting", "Exterior Painting", "Both Interior & Exterior", "Colour Consultation", "Surface Preparation Only"];
const PROPERTY_TYPES = ["Residential – House", "Residential – Apartment/Unit", "Commercial – Office", "Commercial – Retail", "Other"];
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [progressValue, setProgressValue] = useState(0);
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSubmitting) {
      setProgressValue(10);
      interval = setInterval(() => {
        setProgressValue(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 500);
    } else if (isSubmitted) {
      setProgressValue(100);
    } else {
      setProgressValue(0);
    }
    return () => clearInterval(interval);
  }, [isSubmitting, isSubmitted]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="size-8 text-green-600" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Request Received</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-[4px]">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="rounded-[4px]" {...field} />
              </FormControl>
              <FormDescription>Enter the name of the person requesting the quote.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="rounded-[4px]" {...field} />
              </FormControl>
              <FormDescription>We'll send the quote and follow-up to this address.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone_number" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0400 123 456" className="rounded-[4px]" {...field} />
              </FormControl>
              <FormDescription>A mobile or landline number we can call for clarification.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="property_type" rules={{
        required: "Please select a property type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Property Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full rounded-[4px]">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PROPERTY_TYPES.map((type, i) => <SelectItem key={type} value={type} data-index={i}>
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="property_address" rules={{
      required: "Property Address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Property Address</FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, Townsville QLD" className="rounded-[4px]" {...field} />
            </FormControl>
            <FormDescription>Where the painting work will be carried out.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_scope" rules={{
      required: "Please select at least one service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Service Scope</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {SERVICE_OPTIONS.map((option, i) => <label key={option} data-index={i} className="flex items-start space-x-3 p-3 border rounded-[4px] hover:bg-accent/50 cursor-pointer transition-colors">
                  <input type="checkbox" className="mt-1 size-4 rounded border-input text-primary focus:ring-primary" checked={field.value?.includes(option)} onChange={e => {
            const checked = e.target.checked;
            const current = field.value || [];
            const updated = checked ? [...current, option] : current.filter((val: string) => val !== option);
            field.onChange(updated);
          }} />
                  <span className="text-sm font-medium leading-none">
                    {option}
                  </span>
                </label>)}
            </div>
            <FormDescription>Select all services you are interested in.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="preferred_start_date" render={({
      field
    }) => <FormItem>
            <FormLabel>Preferred Start Date</FormLabel>
            <FormControl>
              <Input type="date" className="rounded-[4px]" {...field} />
            </FormControl>
            <FormDescription>When would you like the work to begin? (optional)</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details / Questions</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about any special requirements, colours, or questions you have..." className="min-h-[120px] rounded-[4px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-[4px] text-destructive text-sm">
          {submitError}
        </div>}

      <div className="space-y-4 pt-2">
        {isSubmitting && <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Processing request...</span>
              <span>{Math.round(progressValue)}%</span>
            </div>
            <Progress value={progressValue} className="h-2" />
          </div>}
        
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg rounded-[4px]">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sending Request...
            </> : "Request Quote"}
        </Button>
      </div>
    </Form>;
}
export default function StartYourTransformation() {
  return <section id="start-your-transformation" className="flex flex-col lg:flex-row min-h-screen w-full">
      <div className="w-full lg:w-5/12 bg-sidebar text-sidebar-foreground relative flex flex-col">
        <div className="p-8 lg:p-12 xl:p-16 flex flex-col h-full z-10 relative">
          <div className="mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4 text-sidebar-primary">
              Contact Us
            </h2>
            <p className="text-sidebar-foreground/80 text-lg leading-relaxed">
              Ready to elevate your space? Reach out to our team in Townsville for a consultation.
            </p>
          </div>

          <div className="space-y-8 flex-grow">
            <Card className="bg-sidebar-accent border-sidebar-border text-sidebar-foreground shadow-xl rounded-[4px]">
              <CardHeader>
                <CardTitle className="text-xl text-sidebar-primary">Get in Touch</CardTitle>
                <CardDescription className="text-sidebar-foreground/60">
                  Direct lines to our team
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-start gap-4">
                  <MapPin className="size-5 text-sidebar-primary shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Headquarters</p>
                    <p className="text-sm text-sidebar-foreground/80">{SHARED_DATA.address}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <Phone className="size-5 text-sidebar-primary shrink-0" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <Link to={`tel:${SHARED_DATA.phone.replace(/\s/g, '')}`} className="text-sm text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors">{SHARED_DATA.phone}</Link>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Mail className="size-5 text-sidebar-primary shrink-0" />
                  <div>
                    <p className="font-medium">Email</p>
                    <Link to={`mailto:${SHARED_DATA.email}`} className="text-sm text-sidebar-foreground/80 hover:text-sidebar-primary transition-colors">{SHARED_DATA.email}</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Clock className="size-5 text-sidebar-primary shrink-0 mt-1" />
                  <div>
                    <p className="font-medium">Office Hours</p>
                    <p className="text-sm text-sidebar-foreground/80">{SHARED_DATA.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="w-full h-64 lg:h-auto lg:flex-grow rounded-[4px] overflow-hidden border border-sidebar-border shadow-inner bg-sidebar-accent relative min-h-[250px]">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d60297.46929548366!2d146.76897625!3d-19.2589635!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6bd5f8d2765305b1%3A0x500eef17f209e00!2sTownsville%20QLD!5e0!3m2!1sen!2sau!4v1709600000000!5m2!1sen!2sau" width="100%" height="100%" style={{
              border: 0,
              filter: "grayscale(100%) invert(90%) contrast(85%)"
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Office Location Map" className="absolute inset-0 w-full h-full opacity-80 hover:opacity-100 transition-opacity duration-500" />
            </div>
          </div>
        </div>
        
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-sidebar-accent/20 via-transparent to-transparent pointer-events-none" />
      </div>

      <div className="w-full lg:w-7/12 bg-background p-8 lg:p-12 xl:p-20 flex flex-col justify-center">
        <div className="max-w-2xl mx-auto w-full">
          <div className="mb-10">
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
              Start Your Transformation – Request a Quote
            </h1>
            <p className="text-muted-foreground text-lg">
              Request a comprehensive quote for your residential or commercial project.
            </p>
          </div>

          <div className="bg-card rounded-[4px] border shadow-sm p-6 md:p-8">
            <FormProvider formId="quote_form_get_a_quote" sectionName="get-a-quote-section" defaultValues={{
            full_name: "",
            email_address: "",
            phone_number: "",
            property_address: "",
            property_type: "",
            service_scope: [],
            preferred_start_date: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            phone_number: {
              required: true
            },
            property_address: {
              required: true
            },
            property_type: {
              required: true
            },
            service_scope: {
              required: true
            }
          }} submitText="Request Quote" successMessage="Thank you! Your quote request has been received. We will review your details and contact you shortly.">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}